import {
    useContext,
    useState,
    useEffect,
    createContext,
    Dispatch,
    SetStateAction,
} from 'react';
import { useDispatch } from 'react-redux';

import CloseCircleOutlined from '@ant-design/icons/lib/icons/CloseCircleOutlined';
import { Button, Result, Typography } from 'antd';
import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
    useHistory,
    useLocation,
    useParams,
    RouteProps,
} from 'react-router-dom';

import IpRestrictions from 'components/modules/IpRestrictions';
import StyleSwitcher, { StyleNames } from 'components/modules/StyleSwitcher';
import {
    checkEnvironmentForControllable,
    checkEnvironmentIsTao,
    generateUserDomain,
    MFA_APP_LAUNCH_TAB_NAME,
} from 'constants/GlobalConfig';
import { useAppSelector } from 'flex/utils';

import AuthEffectComponent from './components/modules/Auth/AuthEffectComponent';
import GateProgress from './components/modules/GateProgress';
import JobModal from './components/modules/Job/JobModal';
import Message from './components/modules/Message/Message';
import Progress from './components/modules/Progress';
import Apps from './components/presentational/pages/Apps';
import AppTemplate from './components/presentational/pages/AppTemplate';
import { Assessment } from './components/presentational/pages/Assessment';
import {
    AppDeepLinkingResponse,
} from './components/presentational/pages/DeepLinkings/AppDeepLinkingResponse';
import {
    DeepLinkingResponse,
} from './components/presentational/pages/DeepLinkings/DeepLinkingResponse';
import EverydayNote from './components/presentational/pages/EverydayNote';
import Top from './components/presentational/pages/Home';
import Job from './components/presentational/pages/Job';
import Login from './components/presentational/pages/Login/Login';
import Notifications from './components/presentational/pages/Notifications';
import Organization from './components/presentational/pages/Organization';
import Questionnaire from './components/presentational/pages/Questionnaire';
import Security from './components/presentational/pages/Security';
import Usage from './components/presentational/pages/Usage';
import UsersManagement from './components/presentational/pages/UsersManagement';
import BaseLayout from './components/presentational/templates/BaseLayout';
import * as Actions from './flex/Actions';
import AuthLayout from 'components/presentational/templates/AuthLayout';

const { Paragraph, Text } = Typography;

export const LocationContext = createContext<[
    Location['pathname'],
    Dispatch<SetStateAction<Location['pathname']>>
]>(['', () => undefined]);


const App = () => {
    const [previewLocation, setPreviewLocation] = useState<Location['pathname']>('');
    const dispatch = useDispatch();

    useEffect(() => {
        if (checkEnvironmentForControllable()) {
            dispatch(Actions.data.isControllableUser(true));
        } else {
            dispatch(Actions.data.isControllableUser(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!checkEnvironmentIsTao()) return;

        const fav = document.getElementById('fav');
        fav?.setAttribute('href', 'favicon-m.ico');

        const icon = document.getElementById('apple-touch-icon');
        icon?.setAttribute('href', 'logo192-m.png');

        const manifest = document.getElementById('manifest');
        manifest?.setAttribute('href', 'mexcbt-manifest.json');

        const description = document.querySelector('meta[name="description"]');
        description?.setAttribute('content', '実証用学習eポータル');

        const title = document.querySelector('title');
        title?.setAttribute('innerText', '実証用学習eポータル');
    }, []);

    return (
        <Router>
            <LocationContext.Provider value={[previewLocation, setPreviewLocation]}>
                <StyleSwitcher
                    styleName={
                        checkEnvironmentIsTao() ? StyleNames.Mexcbt : StyleNames.Default
                    }
                >
                    <Switch>
                        <Route exact path='/login'>
                            <PublicPage />
                        </Route>
                        <Route path='/error/:tag'>
                            <Error />
                        </Route>
                        <AuthRoute exact path='/verify'>
                            <AuthLayoutPage />
                        </AuthRoute>
                        <PrivateRoute>
                            <ProtectedPage />
                        </PrivateRoute>
                    </Switch>
                    <AuthEffectComponent />
                    <Message />
                </StyleSwitcher>
            </LocationContext.Provider>
        </Router>
    );
};

export default App;


const PrivateRoute: React.FC<RouteProps<string>> = ({ children, ...rest }) => {
    const [, setPreviewLocation] = useContext(LocationContext);
    const session = useAppSelector(state => state.session);
    const checkLoginConnection = useAppSelector(state => state.checkLoginConnection);
    const location = useLocation();
    const dispatch = useDispatch();

    useEffect(() => {
        setPreviewLocation(location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        //Sessionページが必要かどうかをここで判断してよしなに処理
        if (process.env.REACT_APP_HAVE_SESSION === 'true' && !session.payload.is_authenticated) {
            dispatch(Actions.http.connection.authentication.check());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (session.payload.is_ip_restricted) {
        return <IpRestrictions />;
    }

    if (checkLoginConnection.meta.fetch || (!session.payload.is_checked && !session.payload.is_ip_restricted)) {
        return <Progress />;
    }

    return (
        <Route
            {...rest}
            render={() => {
                return session.payload.is_required_session ? (
                    session.payload.is_authenticated ? (
                        (children as JSX.Element)
                    ) : (
                        <GateProgress />
                    )
                ) : (
                    (children as JSX.Element)
                );
            }
            }
        />
    );
};


/** 未認証時のページ */
const PublicPage = ({ ...rest }) => {
    const [previewLocation, setPreviewLocation] = useContext(LocationContext);
    const session = useAppSelector(state => state.session);
    const checkLoginConnection = useAppSelector(state => state.checkLoginConnection);
    const location = useLocation();
    const dispatch = useDispatch();

    useEffect(() => {
        setPreviewLocation(location.pathname);
        if (process.env.REACT_APP_HAVE_SESSION === 'true' && !session.payload.is_authenticated) {
            dispatch(Actions.http.connection.authentication.check());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (session.payload.is_ip_restricted) {
        return <IpRestrictions />;
    }

    return (
        checkLoginConnection.meta.fetch || !session.payload.is_checked ?
            <Progress /> :
            <Route
                {...rest}
                render={({ location }) =>
                    session.payload.is_authenticated ?
                        (
                            <Redirect
                                to={{
                                    pathname: previewLocation && previewLocation !== '/login' ? previewLocation : '/',
                                    state: { from: location },
                                }}
                            />
                        ) :
                        (
                            <Login />
                        )
                }
            />
    );
};

/** 認証時のページ */
const ProtectedPage = () => {
    return (
        <Router>
            <JobModal />
            <Switch>
                {/** BaseLayout を使わなずに表示するページは, */}
                {/** BaseLayOutPage より上に書かないと Not Found が表示されてしまう */}
                <Route component={AppDeepLinkingResponse} path='/app-deeplinkingresponse/confirm/' />
                <Route component={DeepLinkingResponse} path='/deeplinkingresponse/confirm/' />
                <Route component={BaseLayoutPage} />
            </Switch>
        </Router>
    );
};

/**
 * 全体管理者限定のページ用ルート
 *
 * アプリ配信管理・学校管理は useFeatures で全体管理者のみメニューに出しているが、
 * 学校管理画面（-mng ドメイン）から URL を直接開くと画面自体は表示できていた（LGT-4621）。
 * backend は権限外のデータを返さないが、画面上の入力フォームや選択肢から連携情報が見えるため、
 * 学校管理画面ではトップへリダイレクトする。判定はドメインで決まる checkEnvironmentForControllable を使う
 */
const ControlOnlyRoute: React.FC<RouteProps> = (props) => {
    if (!checkEnvironmentForControllable()) {
        return <Redirect to='/' />;
    }
    return <Route {...props} />;
};

/** BaseLayout で表示するページ */
const BaseLayoutPage = () => {
    return (
        <Router>
            <BaseLayout>
                <Switch>
                    <Route component={Top} exact path='/' />
                    <ControlOnlyRoute component={AppTemplate} path='/app-templates' />
                    <Route component={Apps} path='/apps' />
                    <Route component={Assessment} path='/assessment' />
                    <Route component={EverydayNote} path='/everyday-note' />
                    <Route component={Notifications} path='/notifications' />
                    <ControlOnlyRoute component={Organization} path='/organization' />
                    <Route component={Questionnaire} path='/questionnaire/result/:uuid' />
                    <Route component={Questionnaire} path='/questionnaires' />
                    <Route component={Usage} path='/usage' />
                    <Route component={UsersManagement} path='/users' />
                    <Route component={Security} path='/security' />
                    <Route component={Job} path='/worker' />
                    <Route render={() => <h2>Not Found</h2>} />
                </Switch>
            </BaseLayout>
        </Router>
    );
};

/** 認証フロー専用ルート（サインアウト完了時に GateProgress を表示し、リロード後は /login へリダイレクト） */
const AuthRoute: React.FC<RouteProps<string>> = ({ children, ...rest }) => {
    const logoutConnection = useAppSelector((state: any) => state.logoutConnection);
    const checkLoginConnection = useAppSelector(state => state.checkLoginConnection);
    const session = useAppSelector(state => state.session);
    const [isLoggedOut, setIsLoggedOut] = useState(false);
    const dispatch = useDispatch();

    // サインアウト処理の完了を監視し、GateProgress を表示する
    useEffect(() => {
        const { SUCCESS, FAILURE, ERROR } = Actions.statusEnum;
        const { status } = logoutConnection.meta;
        if (status === SUCCESS || status === FAILURE || status === ERROR) {
            setIsLoggedOut(true);
        }
    }, [logoutConnection]);

    // ページリロード時にセッションを確認する
    // FAILURE 時は AuthEffectComponent が /login へリダイレクトする
    // MFA フロー中の場合、AuthEffectComponent は /verify へのリダイレクトをスキップする（無限ループ防止）
    useEffect(() => {
        if (process.env.REACT_APP_HAVE_SESSION === 'true' && !session.payload.is_authenticated) {
            dispatch(Actions.http.connection.authentication.check());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // セッション確認未実行の間は Progress を表示し、children の瞬間表示を防ぐ
    const isSessionCheckPending =
        process.env.REACT_APP_HAVE_SESSION === 'true' &&
        !session.payload.is_authenticated &&
        checkLoginConnection.meta.status === '';

    // FAILURE/ERROR 時は AuthEffectComponent が /login へリダイレクト中のため Progress を継続表示
    const isAuthCheckFailed =
        checkLoginConnection.meta.status === Actions.statusEnum.FAILURE ||
        checkLoginConnection.meta.status === Actions.statusEnum.ERROR;

    return (
        <Route
            {...rest}
            render={() => {
                // IP制限に該当した場合は他の表示より優先する（PrivateRoute・PublicPage と同じ扱い）
                if (session.payload.is_ip_restricted) {
                    return <IpRestrictions />;
                }
                // ログアウト直後はGateProgressを表示（ProtectedPageと同様の処理）
                if (isLoggedOut) {
                    return <GateProgress />;
                }
                // セッション確認前・確認中・FAILURE時はProgressを表示
                // FAILURE時はAuthEffectComponentが /login へリダイレクト中のため、
                // children を瞬間表示しないよう Progress を継続する
                if (isSessionCheckPending || checkLoginConnection.meta.fetch || isAuthCheckFailed) {
                    return <Progress />;
                }
                // SUCCESS後は children を表示
                // ※ MFAフロー中（is_authenticated = false の場合も）childrenを表示する
                return children as JSX.Element;
            }}
        />
    );
};

const AuthLayoutPage = () => {
    return (
        <Router>
            <AuthLayout/>
        </Router>
    );
};

const Error = () => {
    const { tag } = useParams<{ tag: string }>();
    const history = useHistory();
    let messageTitle = 'エラー';
    const messageSubTitle = '';
    let messageComponent = <span></span>;
    switch (tag) {
        case 'invalid_tenant':
            messageTitle = 'テナントエラー';
            break;
        case 'invalid_user':
            messageTitle = 'ユーザーエラー';
            break;
        case 'resource_not_found':
            messageTitle = '該当のリソースが存在しません';
            break;
        case 'result_not_found':
            messageTitle = 'テストの結果が存在しません';
            break;
        case 'db_error':
            messageTitle = 'データベースエラー';
            break;
        case 'error':
        case 'mfa-auth-lock':
        default:
            messageTitle = 'その他のエラー';
            break;
    }

    switch (tag) {
        case 'invalid_tenant':
        case 'invalid_user':
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> テナント情報が誤っている可能性があります
                    </Paragraph>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> サインインするユーザ情報があやまっている可能性があります
                    </Paragraph>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> その他、idpに関連するエラー
                    </Paragraph>
                </>
            );
            break;
        case 'resource_not_found':
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> テストのURLが誤っている可能性があります
                    </Paragraph>
                </>
            );
            break;
        case 'result_not_found':
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> テスト結果のURLが誤っている可能性があります
                    </Paragraph>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> テストを受験していない可能性があります
                    </Paragraph>
                </>
            );
            break;
        case 'db_error':
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> データベース異常です 管理者にお問い合わせください
                    </Paragraph>
                </>
            );
            break;
        case 'mfa-auth-lock':
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' />{' '}
                        コードを複数回間違えたため、しばらく認証できません。一定時間経ったあと、再度認証を行ってください。
                    </Paragraph>
                </>
            );
            break;
        case 'error':
        default:
            messageComponent = (
                <>
                    <Paragraph>
                        <CloseCircleOutlined className='site-result-demo-error-icon' /> 不明なエラーです。管理者にお問い合わせください
                    </Paragraph>
                </>
            );
            break;
    }

    /*
    リソース（TAOの場合はテスト）が存在しない： resource_not_found
    結果が存在しない： result_not_found
    DBエラー： db_error
     */

    // 認証ロックアウト時はボタンを文章の下に表示する。
    // 全体管理(control)はログイン画面へ、学校管理(school)は児童生徒アプリのホームへ遷移させる。
    const isMfaLock = tag === 'mfa-auth-lock';
    // 教材・アプリを新規タブで起動した場合はタブを閉じられるようにする。
    // 起動タブ（児童生徒用フロントが開く）には window.name で目印が付いており、
    // 同一サイト内の遷移では保持されるためリダイレクト後のこの画面でも読める。
    // window.opener は起動→backend→リダイレクトの過程で null になり得るため目印との OR で判定する。
    // window.close() はスクリプトが開いたタブでないと無視されるので、同一タブで遷移してきた場合は従来どおり「戻る」。
    const canCloseTab = window.name.startsWith(MFA_APP_LAUNCH_TAB_NAME) || window.opener !== null;
    const handleLockButtonClick = () => {
        if (canCloseTab) {
            window.close();
        } else if (checkEnvironmentForControllable()) {
            history.push('/login');
        } else {
            window.location.href = generateUserDomain();
        }
    };

    return (
        <Result
            status='error'
            subTitle={messageSubTitle}
            title={messageTitle}
        >
            <div className='desc'>
                <Paragraph>
                    <Text
                        strong
                        style={{
                            fontSize: 16,
                        }}
                    >
                        エラーの発生理由は以下のとおりです。
                    </Text>
                </Paragraph>
                {messageComponent}
            </div>
            {isMfaLock && (
                <div style={{ marginTop: 24, textAlign: 'center' }}>
                    <Button onClick={handleLockButtonClick} type='primary'>
                        {canCloseTab ? '閉じる' : '戻る'}
                    </Button>
                </div>
            )}
        </Result>
    );
};
